import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { HelpCircle } from "lucide-react";

const faqs = [
  {
    q: "¿Cuánto cuesta producir un videopodcast con Galvani?",
    a: "Depende de la periodicidad, formato y postproducción. Tenemos packs mensuales desde 1.500€/mes. La forma más rápida de tener un presupuesto exacto es rellenar el formulario y en 24h te enviamos una propuesta ajustada a tu proyecto.",
  },
  {
    q: "¿Tengo que ir a vuestro estudio o podéis grabar en mi oficina?",
    a: "Ambas opciones son posibles. Nuestro estudio en Madrid está preparado para producciones de máxima calidad, pero si prefieres grabar en tu oficina (o incluso en la de tu cliente) llevamos todo el equipo. La calidad final es idéntica.",
  },
  {
    q: "No tengo tiempo para grabar podcasts todas las semanas. ¿Es viable?",
    a: "Sí. Trabajamos con formatos batch: en una sola sesión de 3-4 horas grabamos 4-6 episodios que se publican a lo largo del mes. Solo necesitas 1 día al mes para tener contenido semanal.",
  },
  {
    q: "No sé de qué hablar ni cómo estructurar los episodios. ¿Podéis ayudar?",
    a: "Sí, la estrategia de contenido está incluida en todos nuestros packs. Definimos contigo temas, invitados y guiones para que llegues al estudio a hablar sin preocuparte por nada más.",
  },
  {
    q: "¿En cuánto tiempo veré resultados?",
    a: "Los primeros resultados de branding y percepción de autoridad se notan en 2-3 meses. Los resultados en generación de leads suelen consolidarse a partir del mes 6, cuando la biblioteca de contenido empieza a hacer efecto compuesto.",
  },
  {
    q: "¿Qué pasa si nunca he hablado ante una cámara?",
    a: "Es lo más habitual. En la primera sesión hacemos una prueba y te damos indicaciones concretas para que salgas natural. La mayoría de nuestros clientes empresariales llegan sin experiencia y en 2-3 episodios ya se sienten cómodos.",
  },
  {
    q: "¿Publicáis vosotros el contenido o me lo entregáis?",
    a: "Elige tú. Podemos entregarte todos los archivos listos para que los suba tu equipo, o encargarnos nosotros de la publicación en YouTube, Spotify, Apple Podcasts, LinkedIn y creación de clips para redes.",
  },
  {
    q: "¿Con qué sectores trabajáis?",
    a: "Trabajamos con empresas B2B de sectores muy diversos: consultoría, SaaS, salud, legal, formación, retail, industrial, agencias… Adaptamos el enfoque a la audiencia de cada cliente.",
  },
  {
    q: "¿Puedo cancelar cuando quiera?",
    a: "Sí. Trabajamos sin permanencia. Nuestro objetivo es que sigas con nosotros porque ves resultados, no por un contrato que te obligue.",
  },
  {
    q: "¿Cómo empezamos?",
    a: "Rellena el formulario de solicitud de propuesta. Te llamamos en menos de 24h laborables para conocer tu proyecto y en 48h tienes una propuesta clara con formatos, timings y presupuesto.",
  },
];

export default function FAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section-spacing bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 border border-purple-200 mb-6">
            <HelpCircle className="w-4 h-4 text-galvani-purple" />
            <span className="text-sm text-galvani-purple font-body font-medium">Preguntas frecuentes</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            Resolvemos tus <span className="gradient-text">dudas</span> antes de que las tengas
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Las preguntas que más nos hacen antes de empezar un proyecto.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-slate-200 rounded-2xl px-6 bg-white hover:border-slate-300 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-heading font-semibold text-galvani-text text-left hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-galvani-textLight text-sm leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-10 text-center text-sm text-galvani-textLight"
        >
          ¿Otra duda?{" "}
          <a
            href="mailto:info@galvanistudios.com"
            className="text-galvani-purple hover:underline font-medium"
          >
            Escríbenos a info@galvanistudios.com
          </a>{" "}
          y te contestamos personalmente.
        </motion.p>
      </div>
    </section>
  );
}
